import { TRACKING_EVENTS } from './trackingEvents';

export interface TrackingEvent {
  name: string;
  properties?: Record<string, any>;
}

declare global {
  interface Window {
    fbq?: any;
    gtag?: any;
    dataLayer?: any[];
    clarity?: any;
    fbPixelInitialized?: boolean;
    gaInitialized?: boolean;
    gtmInitialized?: boolean;
    clarityInitialized?: boolean;
  }
}

export function initializeTracking(gaId?: string, fbId?: string, gtmId?: string, clarityId?: string) {
  // Get IDs from environment variables if not provided
  const googleAnalyticsId = gaId || import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
  const facebookPixelId = fbId || import.meta.env.VITE_FACEBOOK_PIXEL_ID;
  const googleTagManagerId = gtmId || import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;
  const clarityProjectId = clarityId || import.meta.env.VITE_CLARITY_PROJECT_ID;

  // Remove any existing tracking scripts
  document.querySelectorAll('[data-tracking]').forEach(el => el.remove());

  // Initialize Google Tag Manager
  if (googleTagManagerId) {
    try {
      // GTM Base Script
      const gtmScript = document.createElement('script');
      gtmScript.innerHTML = `
         (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
         new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
         j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
         'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
         })(window,document,'script','dataLayer','${googleTagManagerId}');
       `;
      gtmScript.setAttribute('data-tracking', 'gtm');
      document.head.insertBefore(gtmScript, document.head.firstChild);

      window.gtmInitialized = true;
      window.dataLayer = window.dataLayer || [];
      // console.log('GTM initialized with ID:', googleTagManagerId);
    } catch (error) {
      console.error('Failed to initialize Google Tag Manager:', error);
    }
  }

  // Initialize Google Analytics
  if (googleAnalyticsId) {
    try {
      const gaScript = document.createElement('script');
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
      gaScript.async = true;
      gaScript.setAttribute('data-tracking', 'ga');
      document.head.appendChild(gaScript);

      const gaConfigScript = document.createElement('script');
      gaConfigScript.innerHTML = `
         window.dataLayer = window.dataLayer || [];
         function gtag(){dataLayer.push(arguments);}
         gtag('js', new Date());
         gtag('config', '${googleAnalyticsId}');
       `;
      gaConfigScript.setAttribute('data-tracking', 'ga-config');
      document.head.appendChild(gaConfigScript);
      window.gaInitialized = true;
      // console.log('GA4 initialized with ID:', googleAnalyticsId);
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  // Initialize Facebook Pixel
  if (facebookPixelId) {
    try {
      const fbScript = document.createElement('script');
      fbScript.innerHTML = `
         !function(f,b,e,v,n,t,s)
         {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
         n.callMethod.apply(n,arguments):n.queue.push(arguments)};
         if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
         n.queue=[];t=b.createElement(e);t.async=!0;
         t.src=v;s=b.getElementsByTagName(e)[0];
         s.parentNode.insertBefore(t,s)}(window, document,'script',
         'https://connect.facebook.net/en_US/fbevents.js');
         fbq('init', '${facebookPixelId}');
         fbq('track', 'PageView');
       `;
      fbScript.setAttribute('data-tracking', 'fb');
      document.head.appendChild(fbScript);
      window.fbPixelInitialized = true;
      // console.log('✓ Facebook Pixel initialized with ID:', facebookPixelId);
    } catch (error) {
      console.error('Failed to initialize Facebook Pixel:', error);
    }
  }

  // Initialize Microsoft Clarity
  if (clarityProjectId) {
    try {
      const clarityScript = document.createElement('script');
      clarityScript.innerHTML = `
        (function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${clarityProjectId}");
      `;
      clarityScript.setAttribute('data-tracking', 'clarity');
      document.head.appendChild(clarityScript);
      window.clarityInitialized = true;
      // console.log('Clarity initialized with ID:', clarityProjectId);
    } catch (error) {
      console.error('Failed to initialize Clarity:', error);
    }
  }
}

// Helper function to wait for fbq to be ready with retry
function waitForFbq(callback: () => void, maxRetries = 5, retryDelay = 100) {
  if (window.fbq) {
    callback();
    return;
  }

  if (maxRetries > 0) {
    setTimeout(() => {
      waitForFbq(callback, maxRetries - 1, retryDelay);
    }, retryDelay);
  } else {
    console.warn('[trackEvent] fbq never initialized after retries');
  }
}

export function trackEvent(event: TrackingEvent) {
  try {
    // Track in Google Analytics
    if (window.gtag) {
      window.gtag('event', event.name, {
        ...event.properties,
        event_category: 'Quiz',
        non_interaction: false
      });
    }

    // Track in Facebook Pixel - wait for fbq to be ready
    waitForFbq(() => {
      // console.log('[trackEvent] fbq firing event:', event.name, event.properties);
      switch (event.name) {
        case TRACKING_EVENTS.FB_PAGE_VIEW:
          // console.log('[fbq] Tracking PageView');
          window.fbq('track', 'PageView', event.properties);
          break;

        case TRACKING_EVENTS.FB_CUSTOM_QUIZ_START:
          window.fbq('trackCustom', 'QuizStart', event.properties);
          break;

        case TRACKING_EVENTS.FB_CUSTOM_QUIZ_COMPLETE:
          window.fbq('trackCustom', 'QuizComplete', event.properties);
          break;

        case TRACKING_EVENTS.FB_CUSTOM_QUIZ_ABANDONED:
          window.fbq('trackCustom', 'QuizAbandoned', event.properties);
          break;

        case TRACKING_EVENTS.FB_LEAD:
          window.fbq('track', 'Lead', event.properties);
          break;

        case TRACKING_EVENTS.FB_VIEW_CONTENT:
          window.fbq('track', 'ViewContent', event.properties);
          break;

        default:
          if (event.name.startsWith('fb_')) {
            const customName = event.name.replace(/^fb_/, '');
            window.fbq('trackCustom', customName, event.properties);
          }
      }
    });

    // Push to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: event.name,
        ...event.properties
      });
    }

    // Track in Clarity
    if (window.clarity) {
      window.clarity('set', event.name, JSON.stringify(event.properties));
    }
  } catch (error) {
    console.error('Error tracking event:', error, event);
  }
}

// Convenience function to track page views
export function trackPageView(pageName: string, pageProperties?: Record<string, any>) {
  trackEvent({
    name: TRACKING_EVENTS.PAGE_VIEW,
    properties: {
      page_name: pageName,
      page_path: window.location.pathname,
      ...pageProperties
    }
  });
}

// Convenience function to track button clicks
export function trackButtonClick(buttonName: string, buttonProperties?: Record<string, any>) {
  trackEvent({
    name: TRACKING_EVENTS.BUTTON_CLICK,
    properties: {
      button_name: buttonName,
      button_location: window.location.pathname,
      ...buttonProperties
    }
  });
}

// Quiz-specific tracking functions
export function trackQuizStart(quizType: 'patient' | 'caregiver') {
  trackEvent({
    name: TRACKING_EVENTS.QUIZ_STARTED,
    properties: {
      quiz_type: quizType,
      timestamp: new Date().toISOString()
    }
  });

  trackEvent({
    name: TRACKING_EVENTS.FB_CUSTOM_QUIZ_START,
    properties: {
      quiz_type: quizType
    }
  });
}

export function trackQuizCompleted(quizType: 'patient' | 'caregiver', totalTimeMs: number, totalPages: number) {
  trackEvent({
    name: TRACKING_EVENTS.QUIZ_COMPLETED,
    properties: {
      quiz_type: quizType,
      total_time_ms: totalTimeMs,
      total_pages: totalPages,
      timestamp: new Date().toISOString()
    }
  });

  trackEvent({
    name: TRACKING_EVENTS.FB_CUSTOM_QUIZ_COMPLETE,
    properties: {
      quiz_type: quizType,
      total_time_seconds: Math.round(totalTimeMs / 1000),
      total_pages: totalPages
    }
  });
}

export function trackQuizAbandoned(quizType: 'patient' | 'caregiver', abandonedAtPage: number, totalTimeMs: number) {
  trackEvent({
    name: TRACKING_EVENTS.QUIZ_ABANDONED,
    properties: {
      quiz_type: quizType,
      abandoned_at_page: abandonedAtPage,
      total_time_ms: totalTimeMs,
      timestamp: new Date().toISOString()
    }
  });

  trackEvent({
    name: TRACKING_EVENTS.FB_CUSTOM_QUIZ_ABANDONED,
    properties: {
      quiz_type: quizType,
      abandoned_at_page: abandonedAtPage,
      total_time_seconds: Math.round(totalTimeMs / 1000)
    }
  });
}

export function trackQuestionAnswered(pageNum: number, questionText: string, answer: string | string[]) {
  trackEvent({
    name: TRACKING_EVENTS.QUESTION_ANSWERED,
    properties: {
      page_num: pageNum,
      question: questionText,
      answer: Array.isArray(answer) ? answer.join(', ') : answer,
      timestamp: new Date().toISOString()
    }
  });
}

export function trackPageViewed(pageNum: number, pageKey: string) {
  trackEvent({
    name: TRACKING_EVENTS.PAGE_VIEWED,
    properties: {
      page_num: pageNum,
      page_key: pageKey,
      timestamp: new Date().toISOString()
    }
  });
}
